import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "cardCount"];
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { useTheme } from 'foundations-theming/utils';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { useMediaBySize } from 'pricing-pages-ui-components/hooks/useMedia';
import { MerchCardGridLayout } from 'pricing-pages-ui-components/layouts/merch-cards/MerchCardGridLayout';
import { useState } from 'react';
import { HsocMerchCard } from './merch-card-grid/HsocMerchCard';
import HsocTermToggle from './HsocTermToggle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GRID_AREA_NAMES = ['Title', 'Subheading', 'Pricing', 'CTAs', 'Features', 'Footer'];
const MOBILE_CARD_WIDTH = 'min(85vw, 420px)';
export const HsocMerchCardGridLayout = _ref => {
  let {
      children,
      cardCount
    } = _ref,
    gridProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isMd = useMediaBySize('md');
  const theme = useTheme();
  /* Matches the padding="400" the HSoC page wrappers apply on mobile, so the
     negated margin below breaks out to that exact edge (not the viewport edge,
     which would slide under in-app's nav) and the gutter track stays a real
     grid column rather than container padding, which browsers can drop from
     scrollable width at the trailing edge. */
  const mobileGutter = theme.space[400];
  const lastColumn = cardCount + 2;
  return /*#__PURE__*/_jsxs(MerchCardGridLayout, Object.assign({
    gridAreaNames: GRID_AREA_NAMES,
    position: "relative",
    gridAutoColumns: isMd ? 'minmax(0, 1fr)' : MOBILE_CARD_WIDTH,
    gridTemplateColumns: isMd ? undefined : `${mobileGutter} repeat(${cardCount}, ${MOBILE_CARD_WIDTH}) ${mobileGutter}`,
    columnGap: isMd ? '500' : '0',
    inlineSize: "100%",
    maxInlineSize: isMd ? '1200px' : undefined,
    marginInline: isMd ? 'auto' : '0',
    scrollContainerMarginInline: isMd ? undefined : `-${mobileGutter}`,
    scrollContainerScrollPaddingInline: isMd ? undefined : mobileGutter
  }, gridProps, {
    children: [children, !isMd &&
    /*#__PURE__*/
    /* A rendered box, not just an empty track, guarantees the trailing
       gutter counts toward scrollable width in every browser. */
    _jsx(PPUIGridItem, {
      "aria-hidden": true,
      gridColumn: `${lastColumn} / span 1`,
      gridRow: `${GRID_AREA_NAMES[0]}-start / ${GRID_AREA_NAMES[GRID_AREA_NAMES.length - 1]}-end`
    })]
  }));
};
const HsocMerchCardGrid = ({
  cards,
  showTermToggle = true,
  activeTerm: controlledTerm,
  onTermChange,
  helpMeChoose
}) => {
  const [internalTerm, setInternalTerm] = useState('ANNUAL');
  const activeTerm = controlledTerm !== null && controlledTerm !== void 0 ? controlledTerm : internalTerm;
  const isMd = useMediaBySize('md');
  const handleTermChange = term => {
    /* Only track term internally when uncontrolled; when a parent drives
       activeTerm, internalTerm is dead state and setting it would force an
       unnecessary re-render on every toggle. */
    if (controlledTerm === undefined) {
      setInternalTerm(term);
    }
    onTermChange === null || onTermChange === void 0 || onTermChange(term);
  };
  const hasHeader = showTermToggle || Boolean(helpMeChoose);
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    maxInlineSize: isMd ? '1200px' : '450px',
    marginInline: "auto",
    children: [hasHeader && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isMd ? 'row' : 'column',
      justifyContent: isMd ? 'space-between' : 'start',
      alignItems: isMd ? 'center' : 'stretch',
      gap: "400",
      children: [/*#__PURE__*/_jsx(View, {
        children: showTermToggle && /*#__PURE__*/_jsx(HsocTermToggle, {
          activeTerm: activeTerm,
          onTermChange: handleTermChange
        })
      }), helpMeChoose]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: hasHeader ? '600' : '0',
      children: /*#__PURE__*/_jsx(HsocMerchCardGridLayout, {
        cardCount: cards.length,
        children: cards.map((card, index) => /*#__PURE__*/_jsx(HsocMerchCard, Object.assign({}, card, {
          /* Mobile grids reserve column 1 for the leading gutter track,
             so real cards start at column 2 there instead of column 1. */
          columnStart: isMd ? index + 1 : index + 2,
          isFirstCard: index === 0,
          isLastCard: index === cards.length - 1
        }), index))
      })
    })]
  });
};
export default HsocMerchCardGrid;